import React from 'react';

import type { PlaybookAiRunResult } from '@aline/common';
import { PlaybookAiRunResultSchema } from '@aline/common';
import { link, routes } from '@aline/routes';
import { LinearProgress, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { useMatch } from 'react-router-dom';
import { aiThreadIdVar } from 'src/editor/toolbars/right-panel/ai/ai-thread-id';
import {
  filterToRunCategories,
  getPlaybookRunProgress,
  getPlaybookRunSummary,
  PlaybookStatusChip,
} from 'src/editor/toolbars/right-panel/ai/playbooks';
import { Apollo } from 'src/gql/generated';
import { openLink, useDocumentIdFromUrl } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { useDocMentionAccessContext } from '../../../chat-hooks/useMentionAccessContext';
import { useMaybeSelectedPlaybookRun } from '../../../chat-hooks/useSelectedPlaybookRun';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { parseCodeBlockJson } from '../../helpers/parseCodeBlockJson';
import { getCodeBlockInfoFromNode } from '../../remark-plugins/remarkCodeBlock';

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  clickableRoot: {
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    cursor: 'pointer',
    transition: theme.transitions.create('border-color', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  root: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1.5),
  },
  titleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  titleText: {
    minWidth: 0,
  },
}));

export const CodePlaybookRunElement = React.memo(function CodePlaybookRunElement({
  node,
}: MarkdownComponentProps): JSX.Element | null {
  const { classes } = useStyles();
  const codeBlockInfo = getCodeBlockInfoFromNode(node);
  if (!codeBlockInfo) return null;

  const parsed = parseCodeBlockJson(
    codeBlockInfo.code,
    PlaybookAiRunResultSchema,
    'CodePlaybookRunElement',
    { captureErrors: !codeBlockInfo.isNodeStreaming }
  );
  if (!parsed.ok) {
    if (codeBlockInfo.isNodeStreaming) {
      return (
        <div className={classes.root}>
          <PlaybookRunLoadingBody />
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <Typography sx={{ fontWeight: 600 }}>Playbook run unavailable</Typography>
        <Typography className={classes.meta}>Could not display this playbook run.</Typography>
      </div>
    );
  }

  return <PlaybookRunCard result={parsed.data} threadId={codeBlockInfo.threadId} />;
});

function PlaybookRunLoadingBody() {
  const { classes } = useStyles();

  return (
    <div className={classes.body}>
      <div className={classes.titleRow}>
        <Skeleton className={classes.titleText} height={24} variant="text" width={120} />
        <Skeleton height={18} variant="rectangular" width={60} />
      </div>
      <Skeleton height={8} variant="rectangular" width="100%" />
      <Skeleton height={16} variant="text" width={120} />
    </div>
  );
}

function PlaybookRunCard({ result, threadId }: { result: PlaybookAiRunResult; threadId: UUID }) {
  const { classes } = useStyles();
  const { documentId, playbookId } = result;
  const documentIdInView = useDocumentIdFromUrl();
  const selectedPlaybookRunContext = useMaybeSelectedPlaybookRun();
  const documentAccessResult = useDocMentionAccessContext(documentId);
  const isOnChatRoute = Boolean(useMatch({ end: false, path: routes.ChatIndex }));
  /**
   * Live updates arrive via the DocPlaybooksUpdated subscription that
   * ChatPanel registers once per document with a playbook run card, so no
   * polling or per-card subscription is needed here. The summary query carries
   * rule jobs without their proposals, which is all a status needs.
   */
  const { data, loading } = Apollo.useGetDocPlaybookSummaryQuery({
    variables: { documentId, playbookId },
  });

  function openPlaybookRun() {
    /**
     * Panel-local selection: only the ChatPanel hosting this card switches to
     * the run panel. The main chat is a singleton that survives the page ->
     * drawer transition, so the selection stays visible next to the opened
     * document without leaking into other panels rendering the same thread.
     */
    selectedPlaybookRunContext?.selectPlaybookRun({ documentId, playbookId, threadId });

    /**
     * Open the run's document alongside the in-chat run panel so the user can
     * act on the run's suggestions immediately. From the full-page chat,
     * navigate the current tab (rather than spawning a new one) so the
     * document comes into focus and the chat drawer docks to the right with
     * its animation. From any other route (e.g. the drawer next to a
     * different document), open a new tab so the user's current tab is not
     * hijacked. If the document is already open in another tab, that tab is
     * focused instead. Skip when the document is already the one in view.
     */
    if (documentIdInView !== documentId) {
      const documentName =
        documentAccessResult?.__typename === 'Document' ? documentAccessResult.name : undefined;
      aiThreadIdVar(threadId);
      openLink(link.DocumentDetailAi({ documentId }), {
        newTab: !isOnChatRoute,
        title: documentName,
      });
    }
  }

  if (!data?.playbook || loading) {
    return (
      <div className={clsx(classes.root, classes.clickableRoot)} onClick={openPlaybookRun}>
        <PlaybookRunLoadingBody />
      </div>
    );
  }

  const progress = getPlaybookRunProgress(filterToRunCategories(data.playbook.categories));

  return (
    <div className={clsx(classes.root, classes.clickableRoot)} onClick={openPlaybookRun}>
      <div className={classes.body}>
        <div className={classes.titleRow}>
          <Typography noWrap className={classes.titleText} sx={{ fontWeight: 600 }}>
            {data.playbook.title || 'Untitled playbook'}
          </Typography>
          <PlaybookStatusChip status={progress.status} />
        </div>
        <LinearProgress value={progress.percent} variant="determinate" />
        <Typography className={classes.meta}>{getPlaybookRunSummary(progress)}</Typography>
      </div>
    </div>
  );
}
